// ResetPassword — step 3 of the reset flow: choose and confirm a new password.
// Calls auth/reset-password with the phone carried in via router state (the OTP
// was verified server-side by the prior verify-otp step), then returns to Login.

import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import * as yup from 'yup';
import { Button, Field, PasswordInput, PasswordStrength } from '@/components/common';
import AuthLayout from '@/layout/AuthLayout';
import { useResetPasswordMutation } from '@/api/endpoints/authApi';
import type { ApiErrorShape } from '@/api/types';
import { scorePassword } from '@/utils/password';
import AuthBack from './AuthBack';
import { validateAll, validateField, type FormErrors } from './validation';

const schema = yup.object({
  password: yup
    .string()
    .required('error_password_required')
    .test('strength', 'error_password_weak', (value) => scorePassword(value ?? '') >= 3),
  confirm: yup
    .string()
    .required('error_password_required')
    .oneOf([yup.ref('password')], 'error_passwords_must_match'),
});

interface ResetPasswordState {
  dialCode?: string;
  phone?: string;
}

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? {}) as ResetPasswordState;

  const [resetPassword, { isLoading: submitting }] = useResetPasswordMutation();

  const [values, setValues] = useState({ password: '', confirm: '' });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);

  const dialCode = state.dialCode ?? '';
  const phone = state.phone ?? '';

  // The screen is meaningless without a phone from the verify step.
  if (!phone) return <Navigate to='/login' replace />;

  const setValue = (name: 'password' | 'confirm', value: string) => {
    const next = { ...values, [name]: value };
    setValues(next);
    if (submitted) {
      validateField(schema, name, next).then((message) =>
        setErrors((prev) => ({ ...prev, [name]: message })),
      );
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    const nextErrors = await validateAll(schema, values);
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    try {
      await resetPassword({
        countryCode: dialCode,
        mobileNumber: phone,
        password: values.password,
      }).unwrap();
      toast.success(t('toast_password_reset'));
      navigate('/login');
    } catch (err) {
      toast.error((err as ApiErrorShape)?.msg ?? t('error_generic'));
    }
  };

  return (
    <AuthLayout variant='login'>
      <AuthBack onClick={() => navigate('/verify-otp')} />
      <div className='auth-page'>
        <header className='auth-page__header'>
          <h1 className='auth-page__title'>{t('text_reset_your_password')}</h1>
          <p className='auth-page__subtitle'>{t('text_reset_password_subtitle')}</p>
        </header>

        <form className='auth-page__form' onSubmit={handleSubmit} noValidate>
          <Field
            label={t('field_new_password')}
            htmlFor='rp-password'
            error={errors.password ? t(errors.password) : undefined}
          >
            <PasswordInput
              id='rp-password'
              placeholder={t('field_enter_password')}
              autoComplete='new-password'
              value={values.password}
              onChange={(e) => setValue('password', e.target.value)}
              error={!!errors.password}
            />
            <PasswordStrength password={values.password} />
          </Field>

          <Field
            label={t('field_confirm_password')}
            htmlFor='rp-confirm'
            error={errors.confirm ? t(errors.confirm) : undefined}
          >
            <PasswordInput
              id='rp-confirm'
              placeholder={t('field_enter_password')}
              autoComplete='new-password'
              value={values.confirm}
              onChange={(e) => setValue('confirm', e.target.value)}
              error={!!errors.confirm}
            />
          </Field>

          <Button type='submit' size='lg' loading={submitting} className='auth-page__submit'>
            {t('button_confirm')}
          </Button>

          <p className='auth-page__alt'>
            <button type='button' className='auth-page__link' onClick={() => navigate('/login')}>
              {t('text_go_to_login')}
            </button>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
